import type { Clock, IdentityPort } from "./ports";

/**
 * The two ways a host speaks for the buyer, as identity ports.
 *
 * `hostTokens`: the host answers every token request (Link in Bio from its
 * own identity frame, the Lissen app from its API). The checkout runs no
 * identity connection of its own, so the port has no broker.
 *
 * `withHandedToken`: the older hand-over, kept for partner pages and the
 * hosted page the app opens. The host hands over one token (or promises
 * one); it is the first answer, and the checkout's own broker answers
 * everything after it.
 */

/** What the host is asked: the session is the one mounted, so it is implied. */
export type HostTokenRequest = { consents: Array<string>; reverify: boolean };

export function hostTokens(
  ask: (request: HostTokenRequest) => Promise<string>,
): IdentityPort {
  return {
    handed: true,
    checkoutToken: async (request) => ({
      token: await ask({
        consents: request.consents ?? [],
        reverify: request.reverify === true,
      }),
    }),
  };
}

/** How long a promised host token may take, counted from when the checkout
 *  was built, before the checkout resolves the buyer itself. */
export const HANDED_TOKEN_WAIT_MS = 5000;

export function withHandedToken(
  port: IdentityPort,
  handed: string | Promise<string | null>,
  clock: Clock,
  waitMs = HANDED_TOKEN_WAIT_MS,
): IdentityPort {
  const deadline = clock.now() + waitMs;
  let used = false;
  return {
    ...port,
    handed: true,
    checkoutToken: (request) => {
      if (used) return port.checkoutToken(request);
      used = true;
      // No token (or none in time) is a refusal: the core then resolves the
      // buyer through the broker exactly as if nothing had been handed.
      return new Promise((resolve, reject) => {
        const timer = clock.setTimeout(
          () => reject(new Error("handed_token_late")),
          Math.max(0, deadline - clock.now()),
        );
        void Promise.resolve(handed).then(
          (token) => {
            clock.clearTimeout(timer);
            if (token) resolve({ token });
            else reject(new Error("no_handed_token"));
          },
          (error: unknown) => {
            clock.clearTimeout(timer);
            reject(
              error instanceof Error ? error : new Error("no_handed_token"),
            );
          },
        );
      });
    },
  };
}
